import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { differenceInDays, format } from "date-fns";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { theme } from "@answersnow/pkg-theme";
import { ListChecks } from "@phosphor-icons/react";
import { convertTokens } from "@date-fns/upgrade/v2";
import { DropzoneDialogBase } from "mui-file-dropzone";
import type { PhaseTrackerStep } from "../../Drawers/PhaseTracker";
import PhaseTracker from "../../Drawers/PhaseTracker";
import MarkAssessmentCompleteDialog from "../../Dialogs/MarkAssessmentCompleteDialog";
import OnboardingActionButton, {
  getPhaseTrackerStepButton,
  type ClientContext,
  type TaskHandlers,
  type LoadingState,
} from "./OnboardingActionButton";
import {
  getClientTaskList,
  getClientTaskListLoading,
  getCustomerDetails,
  getUserPermissionsList,
  getUser,
  isDirector as getIsDirector,
  getCustomerVerified,
  getMarkTaskCompleteLoading,
  getCustomerUserId,
  getResendingLoginEmail,
  isClinician as getIsClinician,
  getTreatmentPlanLoading,
  getUserId,
} from "../../../../selectors";
import { uploadToS3 } from "utils/aws/uploadToS3";
import { getClientDocumentById, updateDocument } from "api/customers";
import useVerifyClientDocument from "hooks/api/documents/useVerifyClientDocument";
import actions from "../../../../actions";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientTask {
  id: string | number;
  task: string;
  completed: boolean;
  phase_3?: boolean;
  start_date?: string;
  completed_date?: string | null;
  task_id?: number;
  client_id?: string;
  prior_step_id?: number | null;
  days_until_warning?: number;
  days_until_critical?: number;
}

// ─── Task definitions ─────────────────────────────────────────────────────────

export const taskDefinitions: Record<
  string,
  { title: string; description: string; note?: string }
> = {
  client_invited: { title: "Client Invited", description: "" },
  client_logged_in: {
    title: "Client Logged In",
    description: "Indicates that the client has logged in to the portal for the first time.",
  },
  orientation_complete: { title: "Orientation Call Complete", description: "" },
  diagnosis_uploaded: {
    title: "Diagnosis Uploaded",
    description:
      "AnswersNow requires an official Autism diagnosis to provide ABA services. Work with the client's family and physician to get this document.",
    note: "Private Pay clients do not need a diagnosis and can begin therapy immediately.",
  },
  clinician_assigned: {
    title: "Clinician Assigned",
    description:
      "Select a clinician that is available and assign them to the client within 2 days.",
  },
  pre_auth_signed: {
    title: "Pre-Auth Signed",
    description:
      "After the pre-auth has been signed, submit it to insurance so that they can review the document.",
  },
  pre_auth_submitted: {
    title: "Pre-Authorization Submitted",
    description: "Submit the pre-authorization to insurance for approval.",
  },
  pre_auth_received: {
    title: "Pre-Authorization Received",
    description:
      "This step will be marked complete automatically when a 'Pre' Authorization has been entered.",
    note: "Clinical team will be in contact to let you know when approval is obtained -- you are likely to be scheduled out in advanced.",
  },
  assessment_scheduled: {
    title: "Assessment Scheduled",
    description:
      "Your initial assessment call will be scheduled by the AnswersNow admin team. We will notify you when it is scheduled.",
  },
  first_assessment_completed: {
    title: "First Assessment Completed",
    description:
      "This task will be marked complete automatically upon the completion of the first Assessment Call.",
  },
  assessment_process_completed: {
    title: "Assessment Calls Completed",
    description:
      "To be marked complete when clinician has all information necessary to complete the treatment plan.",
  },
  re_auth_initiated: {
    title: "Re-Auth Process Initiated",
    description:
      "Please click below to acknowledge that the re-authorization process for this client has been triggered, and that you will begin working on a new treatment plan as soon as possible.",
  },
  treatment_plan_submitted: {
    title: "Treatment Plan Submitted to Clinical",
    description: "Clinical needs to review the treatment plan before it can be approved.",
  },
  treatment_plan_approved: {
    title: "Treatment Plan Approved by Clinical",
    description: "",
  },
  ongoing_auth_signed: {
    title: "Ongoing Authorization Signed",
    description: "Now that the treatment plan has been approved, sign the ongoing authorization.",
  },
  ongoing_auth_submitted: {
    title: "Ongoing Authorization Submitted",
    description:
      "Submit the ongoing authorization and treatment plan to client's primary insurance company.",
  },
  treatment_plan_review_scheduled: {
    title: "Treatment Plan Review with Client Scheduled",
    description:
      "Coordinate with the client to schedule a time to review the treatment plan that has been created from the assessment.",
  },
  treatment_plan_review_completed: {
    title: "Treatment Plan Review with Client Completed",
    description: "Review treatment plan with client. Be prepared for questions they might have.",
  },
  first_therapy_call_scheduled: {
    title: "First Therapy Call Scheduled",
    description: "Communicate with the client to schedule their first therapy call.",
  },
  ongoing_auth_received: {
    title: "Ongoing Authorization Received",
    description:
      "Waiting for the ongoing authorization to be approved and returned by insurance company. This step will be marked complete automatically when an 'Ongoing' Insurance Authorization has been entered.",
    note: "If this is a Private Pay client, enter an Ongoing-Auth to activate billing codes for this client.",
  },
  first_therapy_call_completed: {
    title: "First Therapy Call Completed",
    description:
      "Meet with the client and have their first therapy session with AnswersNow. Once the first therapy call has been completed, the client will move from Phase 2 to Phase 3.",
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getDaysInCurrentStep = (clientTaskList: ClientTask[], currentStepIndex: number): number => {
  if (currentStepIndex < 0 || currentStepIndex >= clientTaskList.length) return 0;
  const currentTask = clientTaskList[currentStepIndex];
  if (currentTask.start_date) {
    return differenceInDays(new Date(), new Date(currentTask.start_date));
  }
  return 0;
};

const mapClientTaskListToPhaseTrackerSteps = (
  clientTaskList: ClientTask[],
  handlers: TaskHandlers,
  clientContext: ClientContext,
  loadingState: LoadingState
): PhaseTrackerStep[] => {
  if (!clientTaskList?.length) return [];
  const currentStepIndex = clientTaskList.findIndex((t) => !t.completed);

  const treatmentPlanApproved = clientTaskList.some(
    (t) => t.task === "treatment_plan_approved" && t.completed
  );

  return clientTaskList.map((task, index) => {
    const def = taskDefinitions[task.task];
    const title = def?.title ?? task.task;
    const description = [def?.description, def?.note].filter(Boolean).join(" ");
    const isCurrent = index === currentStepIndex;
    const isUpdateablePlan =
      task.task === "treatment_plan_submitted" && task.completed && !treatmentPlanApproved;
    const button =
      (isCurrent && !task.completed) || isUpdateablePlan
        ? getPhaseTrackerStepButton(task, clientContext, handlers, loadingState)
        : null;
    const subtitle =
      task.completed && task.completed_date
        ? `Completed ${format(
            new Date(task.completed_date),
            convertTokens("MMM D, YYYY @ hh:mm a")
          )}`
        : undefined;

    return {
      id: String(task.id),
      title,
      description: description || undefined,
      subtitle,
      completed: task.completed,
      isCurrent,
      completedDate: task.completed_date ?? undefined,
      button: button
        ? {
            label: button.label,
            onClick: button.onClick,
            loading: button.loading,
            icon: button.icon,
          }
        : undefined,
    };
  });
};

export { mapClientTaskListToPhaseTrackerSteps };

// ─── Component ────────────────────────────────────────────────────────────────

interface OnboardingStatusProps {
  onOpenAssignClinician?: (type: string, value: boolean) => void;
  customerLoaded?: boolean;
}

const OnboardingStatus: React.FC<OnboardingStatusProps> = ({
  onOpenAssignClinician,
  customerLoaded = true,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const clientTaskList: ClientTask[] = useSelector(getClientTaskList);
  const clientTaskListLoading: boolean = useSelector(getClientTaskListLoading);
  const customerDetails = useSelector(getCustomerDetails);
  const userPermissions = useSelector(getUserPermissionsList);
  const currentUser = useSelector(getUser);
  const director = useSelector(getIsDirector);
  const verified = useSelector(getCustomerVerified);
  const markTaskComplete = useSelector(getMarkTaskCompleteLoading);
  const customerUserId = useSelector(getCustomerUserId);
  const resendingLoginEmail = useSelector(getResendingLoginEmail);
  const clinician = useSelector(getIsClinician);
  const treatmentPlanUploading = useSelector(getTreatmentPlanLoading);
  const clinicianUserId = useSelector(getUserId);

  const { verifyDocument, verifyIsLoading: treatmentPlanApproving } = useVerifyClientDocument({
    onSuccess: () => {
      const clientId = customerDetails?.details?.client_id;
      if (clientId) dispatch(actions.loadCustomerDetails(clientId));
    },
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [phaseTrackerOpen, setPhaseTrackerOpen] = useState(false);
  const [assessmentCompleteOpen, setAssessmentCompleteOpen] = useState(false);
  const [treatmentPlanOpen, setTreatmentPlanOpen] = useState(false);
  const [updateTreatmentPlanOpen, setUpdateTreatmentPlanOpen] = useState(false);
  const [updateTreatmentPlanTarget, setUpdateTreatmentPlanTarget] = useState<any>(undefined);
  const [fileObjects, setFileObjects] = useState<any[]>([]);

  const handleSaveTreatmentPlan = async (files: any[]) => {
    setTreatmentPlanOpen(false);
    const file = files[0]?.file;
    if (!file) return;
    const details = customerDetails?.details;
    if (!details?.cognito_id || !details?.client_id) return;
    const fileUUID = uuidv4();
    try {
      const upload = await uploadToS3({
        file,
        key: `${details.cognito_id}/treatment_plan-${fileUUID}.pdf`,
        params: { ACL: "authenticated-read", ContentType: file.type },
      });
      dispatch(
        actions.uploadTreatmentPlan({
          filename: upload.Key.split("/")[1],
          key: upload.Key,
          display_filename: "treatment_plan.pdf",
          clientId: details.client_id,
          type: "TREATMENT_PLAN",
          internal: false,
          billing_related: false,
          clinicianUserId,
          file_size: file.size,
          uploaded_by: clinicianUserId,
        })
      );
    } catch (e) {
      console.error("Failed to upload treatment plan:", e);
    }
  };

  const handleUpdateTreatmentPlan = async (files: any[]) => {
    setUpdateTreatmentPlanOpen(false);
    const file = files[0]?.file;
    if (!file || !updateTreatmentPlanTarget) return;

    const documentId = updateTreatmentPlanTarget.document_id;
    if (!documentId) {
      console.error("Cannot update treatment plan: missing document_id");
      return;
    }

    const clientId = customerDetails?.details?.client_id;
    if (!clientId) {
      console.error("Cannot update treatment plan: missing client_id");
      return;
    }
    dispatch(actions.updateTreatmentPlan());
    try {
      const docResponse = await getClientDocumentById(documentId);
      const existingKey = docResponse.data.document.key;
      await uploadToS3({
        file,
        key: existingKey,
        params: { ACL: "authenticated-read", ContentType: file.type },
      });
      await updateDocument(clientId, {
        id: documentId,
        type: "TREATMENT_PLAN",
        filename: existingKey.split("/")[1],
        display_filename: file.name,
        file_size: file.size,
        uploaded_by: clinicianUserId,
      });
      dispatch(actions.updateTreatmentPlanSuccess());
      dispatch(actions.loadCustomerDetails(clientId));
    } catch (e) {
      console.error("Error updating treatment plan:", e);
      dispatch(actions.updateTreatmentPlanFail());
    }
  };

  const clientContext: ClientContext = {
    customerDetails,
    userPermissions,
    currentUser,
    isDirector: director,
    verified,
    isActive: customerDetails?.details?.is_active,
    isClinician: clinician,
  };

  const handlers: TaskHandlers = {
    onMarkClientTaskComplete: ({ clientId, clientTaskId }) =>
      dispatch(actions.markClientTaskComplete({ clientId, clientTaskId })),
    onResendLoginEmail: () => dispatch(actions.resendLoginEmail(customerUserId)),
    onOpenAssignClinician,
    onToggleScheduleClientVideo: () => {
      history.push("/clinician-schedule/create-event");
    },
    onToggleAssessmentComplete: () => setAssessmentCompleteOpen((prev) => !prev),
    onToggleTreatmentPlanDialog: () => setTreatmentPlanOpen((prev) => !prev),
    onOpenUpdateTreatmentPlan: (plan) => {
      setUpdateTreatmentPlanTarget(plan);
      setUpdateTreatmentPlanOpen(true);
    },
    onApproveTreatmentPlan: () => {
      const plan = customerDetails?.treatmentPlans?.find((p: any) => !p.approved);
      if (plan?.document_id) {
        verifyDocument(plan.document_id);
      }
    },
  };

  const loadingState: LoadingState = {
    markTaskComplete,
    resendingLoginEmail,
    treatmentPlanUploading,
    treatmentPlanApproving,
  };

  const currentStepIndex = clientTaskList?.findIndex((task) => !task.completed) ?? -1;
  const currentTask = currentStepIndex >= 0 ? clientTaskList[currentStepIndex] : null;
  const treatmentPlanApproved = clientTaskList?.some(
    (t) => t.task === "treatment_plan_approved" && t.completed
  );
  const completedTreatmentPlanTask =
    currentTask?.task !== "treatment_plan_submitted" && !treatmentPlanApproved
      ? clientTaskList?.find((t) => t.task === "treatment_plan_submitted" && t.completed) ?? null
      : null;
  const totalSteps = clientTaskList?.length ?? 0;
  const currentStepNumber = currentStepIndex >= 0 ? currentStepIndex + 1 : totalSteps;
  const taskDef = currentTask ? taskDefinitions[currentTask.task] : null;
  const daysInStep = getDaysInCurrentStep(clientTaskList, currentStepIndex);

  // Show loading skeleton if customer data is not yet loaded
  if (!customerLoaded) {
    return (
      <Grid
        container
        component="section"
        padding={"12px 8px 8px 8px"}
        borderRadius={4}
        gap={0}
        spacing={0}
        bgcolor={"grey.50"}
        role="status"
        aria-live="polite"
        aria-label="Loading onboarding status"
        data-testid="onboarding-status-loading"
        sx={{ maxWidth: "1200px", mx: "auto", my: 7 }}
      >
        <Grid xs={12} margin={2}>
          <Skeleton variant="text" width="20%" height={40} />
        </Grid>

        <Grid xs={12}>
          <Box padding={7} borderRadius={3} bgcolor={"background.paper"}>
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (clientTaskListLoading || !clientTaskList || clientTaskList.length === 0) return null;

  const allComplete = currentStepIndex === -1;

  return (
    <>
      <Grid
        container
        component="section"
        padding={"16px 8px 8px 8px"}
        borderRadius={4}
        gap={2}
        spacing={0}
        bgcolor={"grey.50"}
        aria-labelledby="onboarding-status-heading"
        data-testid="onboarding-status"
        sx={{ maxWidth: "1200px", mx: "auto", mb: 7, mt: 0 }}
      >
        <Grid
          xs={12}
          display="flex"
          alignItems="center"
          gap={2}
          paddingX={2}
          data-testid="onboarding-status-header"
        >
          <Typography
            id="onboarding-status-heading"
            component="h3"
            variant="h4"
            sx={{ fontWeight: 500 }}
          >
            Onboarding Status
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            aria-label={`Step ${currentStepNumber} of ${totalSteps} in onboarding`}
            data-testid="onboarding-step-counter"
          >
            Step {currentStepNumber} of {totalSteps}
          </Typography>
        </Grid>

        <Grid xs={12}>
          <Box
            padding={7}
            borderRadius={3}
            bgcolor={"background.paper"}
            border="1px solid"
            borderColor="divider"
          >
            {allComplete ? (
              <Box
                textAlign="center"
                py={4}
                role="status"
                aria-live="polite"
                data-testid="onboarding-complete"
              >
                <Typography variant="h6" color="success.main" gutterBottom>
                  All onboarding steps completed!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This client has completed all onboarding tasks.
                </Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="caption" fontWeight={500} color="text.secondary">
                    Current Step:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    aria-label={`${daysInStep} days in current step`}
                    data-testid="onboarding-days-in-step"
                  >
                    Days in current step: <strong aria-hidden="true">{daysInStep}</strong>
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="h5" fontWeight={400} data-testid="onboarding-step-title">
                    {taskDef?.title || currentTask?.task}
                  </Typography>
                  {(taskDef?.description || taskDef?.note) && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      data-testid="onboarding-step-description"
                    >
                      {taskDef?.description}
                      {taskDef?.description && taskDef?.note ? " " : ""}
                      {taskDef?.note}
                    </Typography>
                  )}
                </Box>

                <Box
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  role="group"
                  aria-label="Step actions"
                  data-testid="onboarding-action-area"
                  sx={{ mt: 2 }}
                >
                  {currentTask && (
                    <OnboardingActionButton
                      task={currentTask}
                      clientContext={clientContext}
                      handlers={handlers}
                      loadingState={loadingState}
                    />
                  )}
                  {completedTreatmentPlanTask &&
                    currentTask?.task !== "treatment_plan_approved" && (
                      <OnboardingActionButton
                        task={completedTreatmentPlanTask}
                        clientContext={clientContext}
                        handlers={handlers}
                        loadingState={loadingState}
                      />
                    )}
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<ListChecks weight="duotone" />}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      setPhaseTrackerOpen(true);
                    }}
                    aria-label="View all onboarding steps"
                    data-testid="view-all-steps-button"
                  >
                    View All Steps
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={phaseTrackerOpen}
        onClose={() => setPhaseTrackerOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : 480,
            maxWidth: "100%",
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
            overflow: "hidden",
            p: isMobile ? 0 : 8,
          }}
        >
          <PhaseTracker
            title="Onboarding Status"
            steps={mapClientTaskListToPhaseTrackerSteps(
              clientTaskList || [],
              handlers,
              clientContext,
              loadingState
            )}
            onClose={() => setPhaseTrackerOpen(false)}
          />
        </Box>
      </Drawer>

      <MarkAssessmentCompleteDialog
        open={assessmentCompleteOpen}
        onToggleAssessmentComplete={() => setAssessmentCompleteOpen(false)}
        onSubmitMarkAssessmentComplete={() => {
          dispatch(
            actions.updateUserInsuranceStatus({
              status: "ASSESSMENT_COMPLETED",
              clientId: customerDetails?.details?.client_id,
            })
          );
          setAssessmentCompleteOpen(false);
        }}
        isClinician={clinician}
      />

      <DropzoneDialogBase
        dialogTitle="Upload Treatment Plan"
        open={treatmentPlanOpen}
        filesLimit={1}
        acceptedFiles={[".pdf"]}
        fileObjects={fileObjects}
        showPreviews={false}
        showPreviewsInDropzone={true}
        maxFileSize={15728640}
        onClose={() => {
          setTreatmentPlanOpen(false);
          setFileObjects([]);
        }}
        onDelete={() => setFileObjects([])}
        onAdd={(newFiles) => setFileObjects(newFiles)}
        onSave={async () => {
          await handleSaveTreatmentPlan(fileObjects);
          setFileObjects([]);
        }}
        submitButtonText="Save"
        dropzoneText="Drag and drop or click here"
        dialogProps={{ style: { zIndex: 40003 } }}
      />

      <DropzoneDialogBase
        dialogTitle="Update Treatment Plan"
        open={updateTreatmentPlanOpen}
        filesLimit={1}
        acceptedFiles={[".pdf"]}
        fileObjects={fileObjects}
        showPreviews={false}
        showPreviewsInDropzone={true}
        maxFileSize={15728640}
        onClose={() => {
          setUpdateTreatmentPlanOpen(false);
          setFileObjects([]);
        }}
        onDelete={() => setFileObjects([])}
        onAdd={(newFiles) => setFileObjects(newFiles)}
        onSave={async () => {
          await handleUpdateTreatmentPlan(fileObjects);
          setFileObjects([]);
        }}
        submitButtonText="Save"
        dropzoneText="Drag and drop or click here"
        dialogProps={{ style: { zIndex: 40003 } }}
      />
    </>
  );
};

export default OnboardingStatus;
